import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Wrench } from 'lucide-react';

import { Filter } from '@/components/filter';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Button } from '@/components/ui/button';

import { useEventHandler } from '@/hooks/use-event-handler';

import { useToolsQuery } from '@/query/tool.query';

import { Permissions } from '@/constants';

import { CreateToolDialog } from './components/create-tool-dialog';
import { ToolDetail } from './components/tool-detail';
import { ToolHeader } from './components/tool-header';
import { ToolSidebarItem } from './components/tool-sidebar';

import useModalStore from '@/store/modal.store';
import { useToolsStore } from '@/store/tools.store';

export default function Tools() {
  const openModal = useModalStore(s => s.openModal);
  const selectedToolId = useToolsStore(state => state.selectedTool?.id);
  const selectTool = useToolsStore(state => state.selectTool);
  const clearSelectedTool = useToolsStore(state => state.clearSelectedTool);
  const { data, isLoading } = useToolsQuery({ page: 1, page_size: 100, query: '' });
  const tools = data?.data || [];
  const [searchTerm, setSearchTerm] = useState('');

  useEventHandler({
    name: 'tool:create',
    eventHandler: () => openModal(() => <CreateToolDialog />),
  });

  const handleFilter = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredTools = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return tools;
    }

    return tools.filter(tool => {
      return [tool.name, tool.description]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(normalizedSearch));
    });
  }, [searchTerm, tools]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (tools.length === 0) {
      clearSelectedTool();
      return;
    }

    if (!selectedToolId) {
      selectTool(tools[0]);
      return;
    }

    const matchingTool = tools.find(tool => tool.id === selectedToolId);

    if (!matchingTool) {
      selectTool(tools[0]);
      return;
    }

    if (matchingTool !== useToolsStore.getState().selectedTool) {
      selectTool(matchingTool);
    }
  }, [clearSelectedTool, isLoading, selectTool, selectedToolId, tools]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 xl:flex-row">
      <div className="flex max-h-[40vh] w-full shrink-0 flex-col overflow-hidden rounded-lg border bg-background xl:max-h-none xl:w-80">
        <div className="border-b bg-muted/30">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 pb-2 pt-3">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold">Tools</span>
              <span className="text-xs text-muted-foreground">({filteredTools.length})</span>
            </div>
          </div>

          <div className="px-3 pb-3">
            <Filter
              placeholder="Search tools..."
              initialValue={searchTerm}
              onFilter={handleFilter}
              onClear={handleClearSearch}
              debounceValue={300}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">Loading tools...</p>
            </div>
          ) : filteredTools.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-muted-foreground">
              <Wrench className="h-8 w-8 opacity-30" />
              <p className="text-sm font-medium">
                {tools.length === 0 ? 'No tools yet' : 'No matching tools'}
              </p>
              <p className="text-xs">
                {tools.length === 0
                  ? 'Connect your first MCP server to get started.'
                  : 'Try a different search term.'}
              </p>
              <Button
                variant={tools.length === 0 ? 'default' : 'outline'}
                size="sm"
                className="mt-1"
                onClick={() => {
                  if (tools.length === 0) {
                    openModal(() => <CreateToolDialog />);
                    return;
                  }

                  handleClearSearch();
                }}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {tools.length === 0 ? 'Add Tool' : 'Clear Search'}
              </Button>
            </div>
          ) : (
            filteredTools.map(tool => (
              <ToolSidebarItem
                key={tool.id}
                tool={tool}
                isSelected={selectedToolId === tool.id}
                onSelect={selectTool}
              />
            ))
          )}
        </div>
      </div>

      <div className="min-h-0 min-w-0 flex-1">
        {selectedToolId ? (
          <div className="flex h-full min-h-[24rem] flex-col overflow-hidden rounded-lg border bg-background">
            <ToolHeader />
            <div className="flex-1 overflow-y-auto">
              <ToolDetail />
            </div>
          </div>
        ) : (
          <div className="flex min-h-[24rem] h-full items-center justify-center rounded-lg border bg-background">
            <div className="px-6 text-center text-muted-foreground">
              <Wrench className="mx-auto mb-4 h-12 w-12 opacity-30" />
              <p className="text-sm font-medium">No tool selected</p>
              <ProtectedComponent requiredPermissions={[Permissions.ToolsCreateUpdate]}>
                <p className="mt-1 text-xs">Select a tool from the sidebar or create a new one.</p>
                <Button
                  className="mt-4"
                  size="sm"
                  onClick={() => openModal(() => <CreateToolDialog />)}
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add Tool
                </Button>
              </ProtectedComponent>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
